/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { createPortal } from 'react-dom';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Button, ModalBody, ModalFooter } from '@carbon/react';

import { Modal, ModalContent } from 'App/Pages/Diagram/features/deployment/modals/modal-base';
import { Payload, useJSONPayload } from 'App/Pages/Diagram/features/deployment/payload';
import { LinkedResources } from 'App/Pages/Diagram/features/deployment/linked-resources';
import { ActionFailed, ProcessApplicationResources } from 'App/Pages/Diagram/features/deployment/inline-notifications';
import { deploymentStates as states, deploymentTypes, modes } from 'App/Pages/Diagram/features/deployment/utils';
import { Deploy, StartInstance } from 'App/Pages/Diagram/features/deployment/actions';
import {
  ClientCredentials,
  useClientCredentialsValidation
} from 'App/Pages/Diagram/features/deployment/client-credentials';
import { TenantId } from 'App/Pages/Diagram/features/deployment/tenant-id';
import { RememberCredentials } from 'App/Pages/Diagram/features/deployment/remember-credentials';
import {
  ClusterSelection,
  useSelectFirstClusterByDefault
} from 'App/Pages/Diagram/features/deployment/cluster-selection';
import { configuredClustersStore, currentDiagramStore, organizationStore, processApplicationStore } from 'stores';
import { DeploymentPolicyWarning, useCheckDeploymentPolicy } from 'features/deploy-to-prod-policy';
import { deploymentStore } from 'App/Pages/Diagram/Deployment/stores';

import Header from './Header';
import { useDeploymentStates } from './hooks';
import StageSelection from './stage-selection';
import SelectedCluster from './selected-cluster';
import { checkIfNoStageSelected, NoStageSelected } from './no-stage-selected';

function DeployModal({ onClose = () => {}, isOpen, mode }) {
  const deploymentType = processApplicationStore.fromAProcessApplication
    ? deploymentTypes.PROCESS_APPLICATION
    : deploymentTypes.DIAGRAM;

  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tenantId, setTenantId] = useState('');

  const deploymentStates = useDeploymentStates(isOpen);

  const [payload, setPayload, isValidPayload] = useJSONPayload();

  const selectFirstClusterByDefaultCondition = !processApplicationStore.fromAProcessApplication;
  useSelectFirstClusterByDefault(
    configuredClustersStore.clusters,
    setSelectedCluster,
    selectFirstClusterByDefaultCondition
  );

  const isNoStageSelectedForProcessApplication = checkIfNoStageSelected(processApplicationStore);

  const { processApplication } = processApplicationStore;
  const isSelectedClusterProd = selectedCluster?.id === processApplication?.defaultProdClusterId;
  const { currentOrganizationId, hasElevatedOrganizationPermissions } = organizationStore;
  const { processApplicationVersionId } = deploymentStore;

  const { deploymentPolicyCheckResult, isLoading } = useCheckDeploymentPolicy({
    isClusterProd: isSelectedClusterProd,
    organizationId: currentOrganizationId,
    hasElevatedOrganizationPermissions,
    processApplicationId: processApplication?.id,
    processApplicationVersionId,
    isOpen
  });

  const isDeploymentDisabled = isLoading || deploymentPolicyCheckResult !== 'NO_RESTRICTIONS';

  const { isValidationEnabled, enableValidation, isInputValidationSuccessful } = useClientCredentialsValidation({
    isOpen,
    clientId,
    clientSecret
  });

  let modalContent;
  if (isNoStageSelectedForProcessApplication) {
    modalContent = <NoStageSelected isOpen={isOpen} mode={mode} deploymentType={deploymentType} onClose={onClose} />;
  } else {
    modalContent = (
      <Modal open={isOpen} preventCloseOnClickOutside>
        <Header mode={mode} deploymentType={deploymentType} onClose={onClose} />
        <ModalBody>
          <ModalContent>
            {processApplicationStore.fromAProcessApplication ? (
              <>
                <StageSelection
                  mode={mode}
                  clusters={configuredClustersStore.clusters}
                  setSelectedCluster={setSelectedCluster}
                  selectedStage={selectedStage}
                  setSelectedStage={setSelectedStage}
                  closeDeployModal={onClose}
                />
                {selectedCluster && <SelectedCluster selectedStage={selectedStage} selectedCluster={selectedCluster} />}
              </>
            ) : (
              <ClusterSelection
                clusters={configuredClustersStore.clusters}
                selectedCluster={selectedCluster}
                setSelectedCluster={setSelectedCluster}
                resetDeploymentState={deploymentStates.reset}
              />
            )}
            <ClientCredentials
              selectedCluster={selectedCluster}
              clientId={clientId}
              setClientId={setClientId}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              isValidationEnabled={isValidationEnabled}
            />
            <TenantId selectedCluster={selectedCluster} tenantId={tenantId} setTenantId={setTenantId} />

            <RememberCredentials
              selectedCluster={selectedCluster}
              clientId={clientId}
              clientSecret={clientSecret}
              setClientId={setClientId}
              setClientSecret={setClientSecret}
            />

            {/** JSON Payload or Start Form */}
            {mode === modes.EXECUTE && (
              <Payload payload={payload} setPayload={setPayload} isValidPayload={isValidPayload} />
            )}

            {/** Information about linked forms, call activity links & business rule task links */}
            <LinkedResources />

            {/** Information that the resources available in the process application will be deployed all at once */}
            <ProcessApplicationResources />

            <DeploymentPolicyWarning
              deploymentPolicyCheckResult={deploymentPolicyCheckResult}
              processApplicationId={processApplication?.id}
              isDraft={!processApplicationVersionId}
              closeModal={onClose}
            />

            {(deploymentStates.currentState === states.DEPLOYMENT_FAILED_NOTIFICATION ||
              deploymentStates.currentState === states.RUN_FAILED_NOTIFICATION) && (
              <ActionFailed
                deploymentType={deploymentType}
                mode={mode}
                failureMessage={deploymentStates.failureMessage}
              />
            )}
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={onClose}>
            Cancel
          </Button>
          {mode === modes.DEPLOY ? (
            <Deploy
              selectedCluster={selectedCluster}
              tenantId={tenantId}
              clientId={clientId}
              clientSecret={clientSecret}
              closeModal={onClose}
              deploymentType={deploymentType}
              diagramId={currentDiagramStore.state?.diagram?.id}
              processApplicationId={processApplicationStore.processApplication?.id}
              processApplicationVersionId={processApplicationVersionId}
              currentState={deploymentStates.currentState}
              transitionToDeployingState={deploymentStates.transitionToDeployingState}
              transitionToDeploymentFailedState={deploymentStates.transitionToDeploymentFailedState}
              disabled={isDeploymentDisabled}
              isInputValidationSuccessful={isInputValidationSuccessful}
              enableValidation={enableValidation}
            />
          ) : mode === modes.EXECUTE ? (
            <StartInstance
              selectedCluster={selectedCluster}
              tenantId={tenantId}
              clientId={clientId}
              clientSecret={clientSecret}
              closeModal={onClose}
              deploymentType={deploymentType}
              diagramId={currentDiagramStore.state?.diagram?.id}
              processApplicationId={processApplicationStore.processApplication?.id}
              jsonPayload={isValidPayload && payload.trim() ? JSON.parse(payload) : {}}
              isJsonPayloadValid={isValidPayload}
              currentState={deploymentStates.currentState}
              transitionToRunningState={deploymentStates.transitionToRunningState}
              transitionToRunFailedState={deploymentStates.transitionToRunFailedState}
              disabled={isDeploymentDisabled}
              isInputValidationSuccessful={isInputValidationSuccessful}
              enableValidation={enableValidation}
            />
          ) : null}
        </ModalFooter>
      </Modal>
    );
  }

  return isOpen ? createPortal(modalContent, document.body) : null;
}

export default observer(DeployModal);
